/// <reference path="./ptlinked.d.ts" />
// The line above (a triple-slash directive, not a runtime import) applies the
// `$.fn.ptlinkedLibrary` ambient type. Each consuming app's tsconfig only `include`s its own
// src/, so packages/shared files are only pulled into that app's TS program via actual
// imports — an unreferenced ambient .d.ts's `declare global` would silently never apply
// outside packages/shared's own type-check otherwise. A real `import './ptlinked'` also
// "works" for tsc (which resolves bare specifiers to .d.ts for type-checking) but breaks at
// runtime: Vite's dev server resolves imports via esbuild, which has no notion of a `.d.ts`
// as a loadable module and fails with "Failed to resolve import". The triple-slash form is a
// plain comment to every tool except tsc, so it can't break bundling.
import { toast } from '@mdvip/shared/hooks/use-toast';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PtlinkedDialog } from './PtlinkedDialog';
import { loadPtlinkedAssets } from './ptlinkedAssetLoader';
import type { ExerciseProgramEvent, PtlinkedInitConfig, ShowDialogEvent } from './types';

export interface WorkoutLibraryWidgetProps {
  config: PtlinkedInitConfig;
  className?: string;
  /**
   * Fires when a physician/staff user clicks "Send" on a program (only renders for those user
   * types with `secure_messaging` on — see PtlinkedCallbacks doc comment). Not exercised by
   * the members POC; providers-app rollout is expected to supply this, e.g. to open a
   * pre-filled Secure Messaging compose window the way legacy did. Left unimplemented (just
   * logged) if the caller doesn't supply one, rather than guessing at a default behavior.
   */
  onSendProgram?: (data: ExerciseProgramEvent) => void;
}

/**
 * Mounts the PTLINKED exercise library into a container div. Data-agnostic per the shared
 * `features/` convention — the consuming app resolves `config` (credentials, user identity)
 * and passes it in; this component never fetches anything itself.
 *
 * Cleanup on unmount currently calls only the vendor's own `destroy()` (guarded — see the
 * `initialized` comment in the effect below — so it's a no-op rather than a thrown error on a
 * mount that unmounts before asset loading finishes). That guard fixes the crash, not the
 * underlying gap: `destroy()` doesn't fully clean up what the plugin creates (some of it goes
 * straight to `document.body`, not this container), so remounting this component within the
 * same page session will visibly duplicate content. That's being held off on a real fix
 * pending vendor confirmation — do not treat visible duplication on remount as a new bug. One
 * consequence worth knowing up front: React's StrictMode double-invokes effects in
 * development, so this duplication will show up on the very first render in dev, not just on a
 * later remount — that's expected for the same reason, not a regression introduced by this
 * component.
 */
export function WorkoutLibraryWidget({ config, className, onSendProgram }: WorkoutLibraryWidgetProps) {
  const { t } = useTranslation('shared');
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [dialogEvent, setDialogEvent] = useState<ShowDialogEvent | null>(null);

  // `onSendProgram` is a caller-supplied prop that may not be referentially stable across
  // renders; going through a ref (rather than the effect's dependency array) means the plugin
  // always calls the latest version without the effect ever needing to re-run for it — the
  // same reason `config` itself isn't in the dependency array below.
  const onSendProgramRef = useRef(onSendProgram);
  useEffect(() => {
    onSendProgramRef.current = onSendProgram;
  }, [onSendProgram]);

  // Re-initializing on every `config` identity change would tear down the user's in-progress
  // filter/search state for no reason; `config` is expected to be stable for the life of the mount.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let cancelled = false;
    // Set true only once `.ptlinkedLibrary(config)` below has actually run — which is also
    // the moment the vendor's own dispatcher attaches its plugin instance via jQuery `.data()`
    // on `container` (synchronous, before `onInit` fires). Calling `destroy()` when that
    // hasn't happened yet — e.g. cleanup running while `loadPtlinkedAssets()` is still
    // pending, reliably reproducible via React StrictMode's double-invoked effects, but not
    // dev-only: any sufficiently fast mount+unmount race hits this — throws
    // "Method destroy does not exist on jQuery.ptlinkedLibrary", since the vendor's dispatcher
    // correctly refuses to call a method on an instance that was never constructed. Closure
    // variable rather than a ref for the same reason `cancelled` is: this effect has empty
    // deps, so there's exactly one instance of it per mount, and both the `.then()` callback
    // and the cleanup function below close over this same scope.
    let initialized = false;

    // Two-argument `.then(onFulfilled, onRejected)` — deliberately not
    // `.then(onFulfilled).catch(onRejected)`. Both forms handle a rejected `loadPtlinkedAssets()`
    // promise, but chaining `.catch()` after `.then()` would ALSO catch anything `onFulfilled`
    // itself throws (e.g. a real bug in the `.ptlinkedLibrary(config)` call below) and
    // misreport it as a failed asset load. The two-argument form only reacts to the original
    // promise's own rejection, which is what we actually want to distinguish. (An earlier
    // version called `loadPtlinkedAssets()` a second time to attach a separate `.catch()` —
    // that's two independent derived promises off the same cached one, so the first `.then()`
    // call's own derived promise still had no rejection handler and surfaced as an *additional*
    // unhandled rejection on every failure, on top of the one this handler logs.)
    loadPtlinkedAssets().then(
      ($) => {
        if (cancelled) return;
        $(container).ptlinkedLibrary({
          ...config,
          dialog_box_type: 'hook',
          onInit: () => {
            if (!cancelled) setStatus('ready');
          },
          onShowDialog: (data) => setDialogEvent(data),
          onSaveProgram: (data) => {
            toast({ title: t('workoutLibrary.programSaved', { title: data.exercise_program_title }) });
          },
          onPrintProgram: (data) => {
            toast({ title: t('workoutLibrary.programPrinted', { title: data.exercise_program_title }) });
          },
          onViewExerciseProgram: (data) => {
            toast({ title: t('workoutLibrary.programOpened', { title: data.title }) });
          },
          onSendProgram: (data) => {
            if (onSendProgramRef.current) {
              onSendProgramRef.current(data);
            } else {
              // Log the program title only, not the full event — `data.user_id` is PII and has
              // no reason to end up in the browser console.
              console.warn(
                'WorkoutLibraryWidget: onSendProgram fired with no handler supplied',
                data.exercise_program_title,
              );
            }
          },
        });
        initialized = true;
      },
      (error: unknown) => {
        if (cancelled) return;
        console.error('WorkoutLibraryWidget: failed to load PTLINKED assets', error);
        setStatus('error');
      },
    );

    return () => {
      cancelled = true;
      // See the class doc comment above — intentionally not doing more than this yet. Guarded
      // by `initialized`: calling `destroy()` when init never ran throws (see comment above),
      // and there's nothing to tear down in that case anyway.
      if (!initialized) return;
      const $container = window.jQuery?.(container);
      $container?.ptlinkedLibrary('destroy');
    };
  }, []);

  return (
    <div className={className}>
      {status === 'loading' && <div className="text-muted-foreground text-sm">{t('common.loading')}</div>}
      {status === 'error' && <div className="text-muted-foreground text-sm">{t('workoutLibrary.loadError')}</div>}
      {/* Vendor's own stylesheet scopes almost every rule under the CLASS selector
          `.ptlinked--application_container`, not just the id — confirmed directly against the
          real loaded stylesheet after the category list rendered as an unstyled vertical block
          without it. Mike's own example sets both `id` and `className` to the same string; drop
          either at your peril. */}
      <div ref={containerRef} id="ptlinked--application_container" className="ptlinked--application_container" />
      <PtlinkedDialog event={dialogEvent} onClose={() => setDialogEvent(null)} />
    </div>
  );
}
